import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Edit2, Play, ToggleRight } from "lucide-react";
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const mockRules = [
  {
    id: 1,
    name: "TSLA Price Watch",
    condition: "TSLA < $200",
    action: "Send push notification + Generate brief",
    frequency: "Real-time",
    cooldown: "30 minutes",
    active: true,
    created: "5 days ago",
    lastTriggered: "2 hours ago",
  },
  {
    id: 2,
    name: "SpaceX Launch Reminder",
    condition: "Event type = 'launch' AND T-1 hour",
    action: "Send email + Create thread + Attach preview brief",
    frequency: "On schedule",
    cooldown: "N/A",
    active: true,
    created: "1 week ago",
    lastTriggered: "3 days ago",
  },
  {
    id: 3,
    name: "FSD Beta Surge",
    condition: "Keyword spike: 'FSD Beta' > 3σ",
    action: "Create pinned thread + Notify community",
    frequency: "Real-time",
    cooldown: "24 hours",
    active: false,
    created: "2 weeks ago",
    lastTriggered: "N/A",
  },
];

const mockExecutions = [
  {
    id: 1,
    rule: "TSLA Price Watch",
    timestamp: "2 hours ago",
    status: "success",
    message: "TSLA crossed below $200. Brief generated, notification sent.",
  },
  {
    id: 2,
    rule: "SpaceX Launch Reminder",
    timestamp: "3 days ago",
    status: "success",
    message: "Falcon 9 launch in 1 hour. Email sent, thread created.",
  },
  {
    id: 3,
    rule: "FSD Beta Surge",
    timestamp: "5 days ago",
    status: "failed",
    message: "Insufficient data. Rule will retry at next spike.",
  },
];

export default function Automations() {
  const [rules, setRules] = useState(mockRules);
  const [selectedRule, setSelectedRule] = useState<number | null>(null);
  const [showBuilder, setShowBuilder] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
    },
  };

  const toggleRule = (id: number) => {
    setRules(rules.map(r => r.id === id ? { ...r, active: !r.active } : r));
  };

  return (
    <div className="space-y-8">
      <Tabs defaultValue="rules" className="w-full">
        <TabsList className="grid w-full grid-cols-2 border-b border-border bg-transparent">
          <TabsTrigger value="rules">Active Rules</TabsTrigger>
          <TabsTrigger value="history">Execution History</TabsTrigger>
        </TabsList>

        {/* Rules Tab */}
        <TabsContent value="rules" className="space-y-6 mt-8">
          {/* Rule Builder */}
          <motion.div
            variants={itemVariants}
            className="border border-border p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Create Automation Rule</h3>
              <motion.button
                whileHover={{ scale: 1.05 }}
                onClick={() => setShowBuilder(!showBuilder)}
                className="text-foreground hover:opacity-70 transition-opacity text-sm"
              >
                {showBuilder ? "Cancel" : "New Rule"}
              </motion.button>
            </div>

            {showBuilder && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="space-y-6 mt-6 pt-6 border-t border-border"
              >
                {/* Rule Name */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase mb-2 block">
                    Rule Name
                  </label>
                  <Input
                    placeholder="e.g., TSLA Price Alert"
                    className="bg-transparent border-b border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-foreground transition-colors py-2 px-0"
                  />
                </div>

                {/* IF Condition */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase mb-2 block">
                    IF Condition
                  </label>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <select className="flex-1 bg-background border border-border text-foreground px-3 py-2 text-sm focus:outline-none focus:border-foreground transition-colors">
                        <option>Select trigger type</option>
                        <option>Market price</option>
                        <option>News keyword</option>
                        <option>Event schedule</option>
                        <option>Sentiment change</option>
                      </select>
                    </div>
                    <Input
                      placeholder="e.g., TSLA < 200"
                      className="bg-transparent border-b border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-foreground transition-colors py-2 px-0"
                    />
                  </div>
                </div>

                {/* THEN Action */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase mb-2 block">
                    THEN Action
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" />
                      <span className="text-sm">Send notification (push/email)</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" />
                      <span className="text-sm">Generate AI brief</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" />
                      <span className="text-sm">Create thread</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" />
                      <span className="text-sm">Add to collection</span>
                    </label>
                  </div>
                </div>

                {/* Frequency & Cooldown */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase mb-2 block">
                      Frequency
                    </label>
                    <select className="w-full bg-background border border-border text-foreground px-3 py-2 text-sm focus:outline-none focus:border-foreground transition-colors">
                      <option>Real-time</option>
                      <option>Hourly</option>
                      <option>Daily</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase mb-2 block">
                      Cooldown
                    </label>
                    <select className="w-full bg-background border border-border text-foreground px-3 py-2 text-sm focus:outline-none focus:border-foreground transition-colors">
                      <option>None</option>
                      <option>30 minutes</option>
                      <option>1 hour</option>
                      <option>24 hours</option>
                    </select>
                  </div>
                </div>

                {/* Quiet Hours */}
                <div>
                  <label className="flex items-center gap-2 mb-2">
                    <input type="checkbox" />
                    <span className="text-sm font-medium">Set quiet hours</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2 ml-6">
                    <Input type="time" placeholder="From" className="text-sm" />
                    <Input type="time" placeholder="To" className="text-sm" />
                  </div>
                </div>

                {/* Submit */}
                <div className="flex gap-3 pt-6 border-t border-border">
                  <Button variant="outline" className="border-border" onClick={() => setShowBuilder(false)}>
                    Cancel
                  </Button>
                  <Button className="ml-auto bg-foreground text-background hover:bg-foreground/90">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Rule
                  </Button>
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Rules List */}
          <motion.div
            className="space-y-4"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {rules.map((rule) => (
              <motion.div
                key={rule.id}
                variants={itemVariants}
                className={`border p-6 transition-colors ${
                  rule.active ? "border-foreground" : "border-border opacity-60"
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-lg font-medium text-foreground">
                        {rule.name}
                      </h3>
                      <Badge variant={rule.active ? "default" : "secondary"}>
                        {rule.active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Created {rule.created}
                    </p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.2 }}
                    onClick={() => toggleRule(rule.id)}
                  >
                    <ToggleRight className={`h-6 w-6 ${rule.active ? "text-foreground" : "text-muted-foreground"}`} />
                  </motion.button>
                </div>

                <div className="space-y-2 mb-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">IF: </span>
                    <code className="text-foreground">{rule.condition}</code>
                  </div>
                  <div>
                    <span className="text-muted-foreground">THEN: </span>
                    <code className="text-foreground">{rule.action}</code>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Frequency: </span>
                    <span className="text-foreground">{rule.frequency}</span>
                    {rule.cooldown !== "N/A" && (
                      <>
                        <span className="text-muted-foreground"> • Cooldown: </span>
                        <span className="text-foreground">{rule.cooldown}</span>
                      </>
                    )}
                  </div>
                  {rule.lastTriggered !== "N/A" && (
                    <div>
                      <span className="text-muted-foreground">Last triggered: </span>
                      <span className="text-foreground">{rule.lastTriggered}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="border-border">
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                  <Button size="sm" variant="outline" className="border-border">
                    <Play className="h-4 w-4 mr-2" />
                    Test
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-border ml-auto"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-6 mt-8">
          <motion.div
            className="space-y-4"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {mockExecutions.map((exec) => (
              <motion.div
                key={exec.id}
                variants={itemVariants}
                className="border border-border p-6 hover:border-foreground/50 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="text-lg font-medium text-foreground">
                      {exec.rule}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {exec.timestamp}
                    </p>
                  </div>
                  <Badge
                    variant={exec.status === "success" ? "default" : "destructive"}
                    className="ml-2"
                  >
                    {exec.status}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">{exec.message}</p>
              </motion.div>
            ))}
          </motion.div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
